import { Button } from "@material-ui/core";
import axios from "axios";
import useAdminRights from "backend/hooks/misc/useAdminRights";
import usePeriodLevels from "backend/hooks/model/usePeriodLevels";
import { IPeriodLevel } from "backend/model/model";
import clsx from "clsx";
import GenericToolbar from "components/toolbars/GenericToolbar";
import { Form, Formik, FormikProps } from "formik";
import { t } from "i18next";
import _ from "lodash";
import useMeaQuery from "qpr-base/apihooks/useMeaQuery";
import {
  getKpiEditingViewValueSettings,
  getKpisAndProjectsManagementHierarchyMode,
} from "qpr-base/configuration";
import { TValueTableSeries } from "qpr-base/configuration/types";
import Anchor from "qpr-base/ui/Anchor";
import Loader from "qpr-base/ui/components/Loader";
import PageContentMenu from "qpr-base/uicomponents/view/PageContentMenu";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ComponentThemes from "../../kpisandprojectsmanagement/themes";
import BasicInfoForm from "../BasicInfoForm";
import ElementLocationForm from "../ElementLocationForm";
import useProjectKPIQueries from "../ProjectAndKPIQueries";
import StatusHandler from "../StatusHandler";
import SubmitKPIForm, { ApproveKPIForm, RejectKPIForm } from "../SubmitKPIForm";
import ValuesForm from "../ValuesForm";
import { mapWorkflowToKPIValues } from "../dataMapping/dataMapperKPI";

import {
  KPI_EMPTY_INITIAL_VALUES as EMPTY_INITIAL_VALUES,
  KPI_FORM_VALIDATION as FORM_VALIDATION,
} from "../initialValuesAndValidators";
import KPIOwnershipForm from "./KPIOwnershipForm";
import MeasurementDetailsForm from "./MeasurementDetailsForm";
import { KPIValues, Value } from "./index.dto";
import useFormStyles, { useButtonStyles } from "./themes";

const KPIsForm = () => {
  const classes = useFormStyles();
  const buttonClasses = useButtonStyles();
  const [GEOptions, setGEOptions] = useState<any[]>([]);
  const [selectedGE, setSelectedGE] = useState<string>("");
  const [themeOptions, setThemeOptions] = useState<any[]>([]);
  const [prioritiesOptions, setPrioritiesOptions] = useState<any[]>([]);
  const [INITIAL_VALUES, setINITIAL_VALUES] =
    useState<KPIValues>(EMPTY_INITIAL_VALUES);
  const [allowedActions, setAllowedActions] = useState<string[]>([
    "submit-enabled",
    "allowedit",
    "approve-enabled",
    "reject-enabled",
  ]);
  const isAdmin = useAdminRights();
  const [status, setStatus] = useState("idle");
  const [loading, setLoading] = useState(false);
  const [editWfId, setEditWfId] = useState<string>("");
  const [editWfType, setEditWfType] = useState<string>("");
  const [approveCount, setApproveCount] = useState<number>(0);
  const originalLocation = useLocation().pathname.split("/");
  const params = new URLSearchParams(window.location.search);
  const [invalidAccess, setInvalidAccess] = useState<boolean>(false);

  const getVisiblePeriods = (periodlevelId: string) => {
    if (!periodlevelId) return [];
    const level = periodlevels?.find((level) => level.id === periodlevelId);
    if (!level) return [];
    return level.periods.map((p) => p.name);
  };

  const selectedMeaElement = useMeaQuery<
    { id: string; name: string; valuesettingid: string; typename: string }[]
  >(`[${params.get("id")}]`, "id, name, valuesettingid, typename", undefined, {
    enabled: params.get("id") !== "",
  });
  const history = useHistory();

  const { data: periodlevels, isLoading: periodlevelsLoading } =
    usePeriodLevels();

  const [editLoading, setEditLoading] = useState<boolean>(false);

  const [meaId, setMeaId] = useState<string>("");
  const {
    data,
    isLoading,
    valueSettings,
    valueSettingsLoading,
    roleData,
    roleLoading,
    units,
    unitsLoading,
  } = useProjectKPIQueries();

  const preferredScorecard = "SC1467324005"; // preferred scorecard (symbol) for new elements

  const hierarchyMode = getKpisAndProjectsManagementHierarchyMode();

  const editType = params.get("type") || "";

  const themeOptionsBySymbol = useMemo(() => {
    // return themeOptions.filter((opt: any) => opt.scorecardid === selectedGE);
    return _.groupBy(themeOptions, "symbol");
  }, [themeOptions]);

  const priorityOptionsBySymbol = useMemo(() => {
    /*
    return prioritiesOptions.filter(
      (opt: any) => opt.scorecardid === selectedGE
    );
    */
    return _.groupBy(prioritiesOptions, "symbol");
  }, [prioritiesOptions]);

  const filterByTheme = (theme: any) => {
    if (!theme || !theme.symbol || !themeOptionsBySymbol[theme.symbol]) return;
    const options = themeOptionsBySymbol[theme.symbol] || [];

    // if (selectedGE)
    //  options = options.filter((opt) => opt.scorecardid === selectedGE);
    const allPriorityAreas = _.flatten(
      options.map((option: any) => option.childobjects.split(","))
    );
    setPrioritiesOptions([
      ...new Set(
        data?.data?.filter((d: any) => {
          if (d.typename !== "Strategic Objective") {
            return false;
          }
          return allPriorityAreas.includes(d.id);
        }) || []
      ),
    ]);
    const allScorecardIds = options.map((option: any) => option.scorecardid);
    const geData: any = [
      ...new Set(
        data?.data?.filter(
          (d: any) => !d.parentid && allScorecardIds.includes(d.scorecardid)
        ) || []
      ),
    ];
    setSelectedGE((prev) => {
      return geData.find((ge: any) => ge.id === prev)?.id || "";
    });
  };

  const getSeries = (): string => {
    const defaultSeries = "ACT,TAR";
    const element = selectedMeaElement.data?.data[0];
    if (!element) return defaultSeries;
    const valset = valueSettings.find(
      (valset: any) => valset.id === element.valuesettingid
    );
    if (!valset) return defaultSeries;
    const valsetConfig = getKpiEditingViewValueSettings().find(
      (vs: any) => vs.name === valset.name
    );
    if (!valsetConfig) return defaultSeries;
    const series = valsetConfig.series
      .map((config: TValueTableSeries) => config.symbol)
      .join(",");
    return series;
  };

  useEffect(() => {
    if (!selectedMeaElement.isLoading) {
      if (params.get("id") !== selectedMeaElement.data?.data[0]?.id) {
        params.set(
          "id",
          selectedMeaElement.data?.data[0]?.id || params.get("id") || ""
        );

        history.replace({
          pathname: window.location.pathname,
          search: params.toString(),
        });
      }
    }
  }, [selectedMeaElement.isLoading, selectedMeaElement.data]);

  useEffect(() => {
    if (!data || isLoading || selectedMeaElement.isLoading) return;
    const selectedMeaElementId = selectedMeaElement.data?.data[0]?.id;
    const selectedWorkflowInstanceId = params.get("wf");
    if (!selectedMeaElementId && !selectedWorkflowInstanceId) {
      // no element or workflow selected, new element is created -> initial selections must be set for that
      const parentElementId = originalLocation[4];
      if (!parentElementId) return; // no parent selected, do not make initial selections
      let parentObjective;
      let parentPerspective;
      let parentTopElement;
      let tmp: any = undefined;
      tmp = data?.data?.find((element: any) => element.id === parentElementId);
      if (!tmp) return;
      if (
        hierarchyMode !== "Scorecards" &&
        preferredScorecard &&
        tmp.scorecardsymbol !== preferredScorecard
      ) {
        // use preferredscorecard if same parentelement exists there
        const parentfromPreferredSC = data.data?.find((element: any) => {
          return (
            element.symbol === tmp.symbol &&
            element.typename === tmp.typename &&
            element.scorecardsymbol === preferredScorecard
          );
        });
        if (parentfromPreferredSC) tmp = parentfromPreferredSC;
      }

      if (tmp.typename === "Perspective") parentPerspective = tmp;
      if (tmp.typename === "Strategic Objective") {
        parentObjective = tmp;
        parentPerspective = data.data?.find(
          (element: any) => element.id === tmp.parentelementid
        );
      }
      if (tmp.id === tmp.topelementid) {
        // top element was selected
        parentTopElement = tmp;
      }

      const newSelections = {
        ...INITIAL_VALUES,
        ...{
          parentelementid:
            parentObjective?.id ||
            parentPerspective?.id ||
            parentTopElement?.id ||
            "",
          location: {
            prioritysymbol: parentObjective?.symbol || "",
            themesymbol: parentPerspective?.symbol || "",
            topelementid: tmp.topelementid,
          },
        },
      };

      setINITIAL_VALUES(newSelections);
      setSelectedGE(tmp.topelementid);
      return;
    }

    const initialize = async () => {
      const initializeEdit = async (): Promise<KPIValues> => {
        setEditLoading(true);
        let wfData: any;
        if (editType !== "existing") {
          const data = await axios.get(
            `workflow/instances/${selectedWorkflowInstanceId}`
          );
          wfData = data.data;
        } else {
          wfData = await axios
            .get(
              `workflow/modifyparams/${selectedMeaElementId}?series=${getSeries()}`
            )
            .catch((err) => {
              if (err.response.status === 401) {
                alert(t("Unauthorized request!"));
                history.goBack();
              }
              return Promise.reject(err);
            });
        }

        setAllowedActions(
          (prevActions) => wfData?.data?.allowedActions || prevActions
        );
        let typeIsDelete: boolean = false;
        if (wfData?.data?.type === "delete") {
          typeIsDelete = true;
          wfData = await axios
            .get(`workflow/modifyparams/${wfData?.data?.meaId}`)
            .catch((err) => {
              if (err.response.status === 401) {
                alert(t("Unauthorized request!"));
                history.goBack();
              }
              return Promise.reject(err);
            });
        }

        if (editType === "existing") {
          setMeaId(selectedMeaElementId || "");
        } else {
          setEditWfId(selectedWorkflowInstanceId || "");
          setEditWfType(typeIsDelete ? "delete" : wfData?.data?.type || "");
        }

        if (wfData?.data?.parameters?.elements.length) {
          const kpiValues = mapWorkflowToKPIValues(
            {
              parentHierarchy: data?.data,
              elementType: "KPI",
              type: wfData?.data?.type,
              meaId: wfData?.data?.meaId,
              parameters: {
                elements: wfData?.data?.parameters?.elements,
              },
            },
            periodlevels || []
          );
          setSelectedGE(kpiValues.location?.topelementid);
          return kpiValues;
        }
        return EMPTY_INITIAL_VALUES;
      };

      const editValues = await initializeEdit();

      if (editValues.meaId) {
        setMeaId(editValues.meaId);
      }

      setINITIAL_VALUES(editValues);
      setEditLoading(false);
    };

    initialize();
  }, [data, isLoading, selectedMeaElement.isLoading, selectedMeaElement.data]);

  useEffect(() => {
    const symbol = undefined; // INITIAL_VALUES?.linkToDubaiPlan?.themeSymbol;
    if (symbol) {
      filterByTheme({ symbol });
    }
  }, [INITIAL_VALUES, themeOptionsBySymbol]);

  const formRef = useRef<FormikProps<any>>(null);

  const setThemes = () => {
    const opts: any[] = [
      ...new Set(
        data?.data?.filter((d: any) => {
          if (d.typename !== "Perspective") return false;
          return !selectedGE || d.topelementid === selectedGE;
        })
      ),
    ];
    setThemeOptions(opts);
  };

  const setPriorities = () => {
    const opts: any[] = [
      ...new Set(
        data?.data?.filter((d: any) => {
          if (d.typename !== "Strategic Objective") return false;
          return d.topelementid === selectedGE;
        })
      ),
    ];
    setPrioritiesOptions(opts);
  };

  useEffect(() => {
    setThemes();
    setPriorities();

    const GEs: any = [
      ...new Set(
        data?.data?.filter((d: any) => d.typename === "Organization") || []
      ),
    ];
    setINITIAL_VALUES((prev) => {
      return prev;
    });

    setGEOptions([...GEs]);
  }, [data, selectedGE]);

  if (
    isLoading ||
    roleLoading ||
    editLoading ||
    unitsLoading ||
    valueSettingsLoading ||
    periodlevelsLoading ||
    selectedMeaElement.isLoading
  ) {
    return <Loader />;
  }

  const disableForm =
    status === "successSubmit" ||
    status === "successAdmin" ||
    status === "successApprove" ||
    status === "successReject" ||
    (!allowedActions.includes("allowedit") &&
      !allowedActions.includes("editAll")) ||
    loading;
  const disableButtons =
    loading ||
    status === "rejecting" ||
    status === "successReject" ||
    status === "successAdmin" ||
    status === "successApprove" ||
    status === "successSubmit" ||
    status === "approving";
  const showSubmit =
    allowedActions.includes("submit-disabled") ||
    allowedActions.includes("submit-enabled");

  const convertFormikValuesToKPIValues = (
    formikmeasurevalues: any[],
    periodlevelid: string,
    periodlevels: IPeriodLevel[] | undefined
  ): Value[] => {
    const periods = periodlevels?.find(
      (level: any) => level.id === periodlevelid
    )?.periods;
    if (!periods) return [];
    const kpiValues: Value[] = [];
    for (let i = 0; i < formikmeasurevalues.length; i += 1) {
      if (formikmeasurevalues[i]) {
        for (const [key, kpivalue] of Object.entries(formikmeasurevalues[i])) {
          kpiValues.push({
            series: key,
            period: periods[i].name,
            value: kpivalue ? (kpivalue as string) : "",
          });
        }
      }
    }
    return kpiValues;
  };

  const getTitle = () => {
    if (editWfId && editWfType) {
      if (editWfType === "modify") return t("kpis.Edit KPI");
      if (editWfType === "create") return t("kpis.Create KPI");
      if (editWfType === "delete") return t("kpis.Delete KPI");
      return t("Undefined operation");
    }
    return editType === "existing" ? t("kpis.Edit KPI") : t("kpis.Create KPI");
  };

  return (
    <>
      <GenericToolbar
        title={getTitle()}
        icon={"/icons/navitems/StrategicKpi.svg"}
        hidePeriodSelector={true}
      />
      <Formik
        initialValues={INITIAL_VALUES}
        validateOnBlur
        innerRef={formRef}
        enableReinitialize
        validateOnChange={false}
        validationSchema={FORM_VALIDATION()}
        onSubmit={(formikData) => {
          const submit = async () => {
            // const noAccess = setAccessErr();

            const noAccess = false;
            if (noAccess) return false;
            setLoading(true);
            formikData.convertedvalues = convertFormikValuesToKPIValues(
              formikData.values,
              formikData.periodlevelid,
              periodlevels
            );
            const res = await SubmitKPIForm(
              formikData,
              "KPI",
              roleData?.role,
              editWfId,
              meaId,
              periodlevels || [],
              isAdmin
            );
            setLoading(false);
            if (res.status === "success") {
              setStatus(res.wfid ? "successSubmit" : "successAdmin");
            } else {
              setStatus(res.status);
            }
          };
          submit();
        }}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          errors,
          setFieldValue,
          submitCount,
          validateForm,
          setTouched,
          touched,
        }) => (
          <div className={classes.screenContainer}>
            <div className={classes.mainContent}>
              <Form>
                <Anchor name="basicInfo" />
                <BasicInfoForm
                  disabled={disableForm}
                  setField={setFieldValue}
                />

                <Anchor name="kpiOwnership" />
                <KPIOwnershipForm
                  GovernmentEntities={GEOptions}
                  selectedGE={selectedGE}
                  setSelectedGE={setSelectedGE}
                  setFieldValue={setFieldValue}
                  disabled={disableForm}
                  GEOwnerSelectionDisabled={false}
                />

                <Anchor name="elementLocation" />
                <ElementLocationForm
                  setFieldValue={setFieldValue}
                  themeOptions={selectedGE ? themeOptionsBySymbol : {}}
                  priorityOptions={selectedGE ? priorityOptionsBySymbol : {}}
                  filterByTheme={filterByTheme}
                  selectedGE={selectedGE}
                  disabled={disableForm}
                />

                <Anchor name="measurementDetails" />
                <MeasurementDetailsForm
                  disabled={disableForm}
                  setField={setFieldValue}
                  unitId={values.measureunitid}
                  valuesettingId={values.valuesettingid}
                  valuesettings={valueSettings}
                  units={units}
                  editing={!!(params.get("id") || params.get("wf"))}
                />
                <Anchor name="values" />
                <ValuesForm
                  isProject={false}
                  projectDates={["", ""]}
                  disabled={disableForm}
                  kpiValues={values.values}
                  periodlevelId={values.periodlevelid}
                  valueSettingId={values.valuesettingid}
                  valuesettings={valueSettings}
                  unitId={values.measureunitid}
                  visiblePeriods={getVisiblePeriods(values.periodlevelid)}
                  units={units}
                />
              </Form>
              {/*
                <div style={{ display: "flex" }}>
                  <pre>{JSON.stringify(values, null, 2)}</pre>
                </div>
                /* <pre>{JSON.stringify(getMetaData(values), null, 2)}</pre> */}
              {/* <div>
              <pre>{JSON.stringify(errors, null, 2)}</pre>
            </div> */}
            </div>
            <div className={classes.stepper}>
              <div>
                <ComponentThemes.PageContentMenu>
                  <PageContentMenu
                    componentData={{
                      items: [
                        { title: t("Basic Information"), anchor: "basicInfo" },
                        {
                          title: t("kpis.KPI Location"),
                          anchor: "elementLocation",
                        },
                        {
                          title: t("kpis.Measurement Details"),
                          anchor: "measurementDetails",
                        },
                        { title: t("kpis.Values"), anchor: "values" },
                      ],
                    }}
                  />
                </ComponentThemes.PageContentMenu>
                <div className={buttonClasses.btncontainer}>
                  <StatusHandler
                    requiredFields={
                      Object.keys(errors).length !== 0 &&
                      (submitCount !== 0 || approveCount !== 0)
                    }
                    invalidAccess={
                      (submitCount !== 0 || approveCount !== 0) && invalidAccess
                    }
                    showSubmit={showSubmit}
                    status={status}
                    type={editType}
                  />
                  {showSubmit ? (
                    <Button
                      className={clsx(
                        buttonClasses.button,
                        buttonClasses.submitButton
                      )}
                      type="submit"
                      onClick={() => handleSubmit()}
                      disabled={
                        loading ||
                        allowedActions.includes("submit-disabled") ||
                        disableButtons
                      }
                    >
                      {!loading ? t("buttons.Submit") : t("buttons.Submitting")}
                    </Button>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                      }}
                    >
                      <div>
                        <Button
                          className={clsx(
                            buttonClasses.button,
                            buttonClasses.submitButton
                          )}
                          onClick={() => {
                            const approve = async () => {
                              setLoading(true);
                              setStatus("approving");

                              const res = await ApproveKPIForm(
                                editWfId,
                                values,
                                "KPI",
                                roleData?.role,
                                periodlevels || [],
                                INITIAL_VALUES,
                                allowedActions.includes("allowedit"),
                                meaId
                              );
                              console.log("APPROVE RESULT", res);
                              setLoading(false);
                              if (res.status === 200) {
                                setStatus("successApprove");
                              } else {
                                setStatus("fail");
                              }
                            };

                            validateForm().then((errs: any) =>
                              setTouched({ ...errs, ...touched })
                            );
                            setApproveCount((prev) => prev + 1);
                            approve();
                          }}
                          disabled={
                            loading ||
                            allowedActions.includes("approve-disabled") ||
                            !allowedActions.includes("approve-enabled") ||
                            disableButtons
                          }
                        >
                          {!(status === "approving")
                            ? t("buttons.Approve")
                            : t("buttons.Approving")}
                        </Button>
                      </div>
                      <div>
                        <Button
                          style={{
                            background: "red",
                          }}
                          className={buttonClasses.button}
                          disabled={
                            loading ||
                            allowedActions.includes("reject-disabled") ||
                            disableButtons
                          }
                          onClick={() => {
                            const reject = async () => {
                              setStatus("rejecting");
                              const res = await RejectKPIForm(editWfId);
                              console.log("REJECT RESULT", res);
                              setLoading(false);
                              if (res.status === 200) {
                                setStatus("successReject");
                              } else {
                                setStatus("fail");
                              }
                            };
                            reject();
                          }}
                        >
                          {!(status === "rejecting")
                            ? t("buttons.Reject")
                            : t("buttons.Rejecting")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Formik>
    </>
  );
};

export default KPIsForm;
